import {
  type LocalDate,
  type Rate,
  parseLocalDate,
  rate as buildRate,
  requireCurrencyCode,
} from '@taler/domain'
import type { Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, principalKey } from '../http/keys.ts'

export interface Currency {
  readonly code: string
  readonly name: string
  readonly symbol: string
  readonly symbolPosition: Schemas['CurrencyResponse']['symbolPosition']
  readonly exponent: number
}

export interface CurrencyRate {
  readonly date: LocalDate
  /** Base currency (EUR, installation-wide) to the requested currency - CUR-4. */
  readonly rate: Rate
}

export const currencyKeys = {
  list: (): CacheKey => principalKey('currencies'),
  rate: (code: string, date?: string): CacheKey => principalKey('currencies', code, date ?? 'latest'),
} as const

export interface CurrenciesRepository {
  list(signal?: AbortSignal): Promise<readonly Currency[]>
  /** The most recent rate at or before `date` (today when omitted); never extrapolated backward. */
  rate(code: string, date?: string, signal?: AbortSignal): Promise<CurrencyRate>
}

export function createCurrenciesRepository(client: TalerClient): CurrenciesRepository {
  return {
    async list(signal) {
      const response = await client.call<Schemas['CurrencyResponse'][]>({
        method: 'GET',
        path: '/api/v1/currencies',
        ...(signal === undefined ? {} : { signal }),
      })
      return response.map(toCurrency)
    },

    async rate(code, date, signal) {
      const response = await client.call<Schemas['CurrencyRateResponse']>({
        method: 'GET',
        path: `/api/v1/currencies/${encodeURIComponent(code)}/rate`,
        query: date === undefined ? {} : { date },
        ...(signal === undefined ? {} : { signal }),
      })
      return {
        date: parseLocalDate(response.date),
        rate: buildRate(response.rate, 'EUR', requireCurrencyCode(response.currency)),
      }
    },
  }
}

function toCurrency(response: Schemas['CurrencyResponse']): Currency {
  return {
    code: response.code,
    name: response.name,
    symbol: response.symbol,
    symbolPosition: response.symbolPosition,
    exponent: response.exponent,
  }
}
